import APIHelper from 'web-app/services/api-helper';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import * as SentryService from 'web-app/services/sentry';
import ENV from 'web-app/config/environment';
import Debounced from 'web-app/util/debounced';
import {OperationType, registerSpan, TraceHeader} from 'web-app/shared/sentry-tracing';

import {createRestClient, type FetchAugmenter, type IAPIHelper, type OnErrorConfig} from './rest-client';
import {APIErrorCode} from '../types';

const ignoredErrorCodes = [
    APIErrorCode.SESSION_EXPIRED, // Your login session has expired
];

/**
 * Takes a timeout in miliseconds and a promise. If the promise hasn't settled before the
 * timeout has run out, returns a rejected promise.
 *
 * @param ms
 * @param promise
 */
const timeoutPromise = <T>(ms: number, promise: Promise<T>): Promise<T> => {
    return new Promise((resolve, reject) => {
        const id = setTimeout(() => reject(new Error('timeout')), ms);
        promise.then(response => {
            clearTimeout(id);
            resolve(response);
        }, reject);
    });
};

/**
 * Returns a promise that resolves after `ms` milliseconds
 */
const wait = (ms: number): Promise<unknown> => {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
};

/**
 * Retries a promise returning function by the passed in delay and retry count.
 *
 * @param delayMs The number of milliseconds to wait before retrying the function
 * @param retryCount The amount of times the function should be retried
 * @param promiseCreator The promise returning function to retry. Must not take any arguments.
 */
const retryPromiseCreatorWithDelay = (delayMs: number, retryCount: number, promiseCreator: () => Promise<any>) => {
    return wait(delayMs)
        .then(promiseCreator)
        .catch(() => {
            if (retryCount > 1) {
                return retryPromiseCreatorWithDelay(delayMs, retryCount - 1, promiseCreator);
            }

            return Promise.reject();
        });
};

const debouncedConnectionCheck = new Debounced(message => {
    const promiseCreator = () => timeoutPromise(5 * 1000, fetch(ENV.releaseUrl));

    return retryPromiseCreatorWithDelay(2000, 3, promiseCreator).catch(() => {
        return GlobalEventActions.updateError.dispatch(message, true);
    });
});

const handleApiError = (message: string, onErrorConfig?: OnErrorConfig) => {
    const checkConnection = onErrorConfig?.checkConnection ?? false;

    if (checkConnection) {
        debouncedConnectionCheck.call(message);
    } else {
        GlobalEventActions.updateError.dispatch(message);
    }
};

const tracingAugment: FetchAugmenter<any> = (url, fetchOptions) => {
    // Register span
    const spanContext = registerSpan({
        op: OperationType.Http,
        description: url.toString(),
    });

    switch (spanContext.type) {
        // If there's an active Transaction then extend headers and finish Span when request returns
        case 'with-transaction': {
            // Add sentry-trace header to outgoing request
            fetchOptions.headers.append(TraceHeader, spanContext.trace);

            return fetcher =>
                fetcher(url, fetchOptions, {
                    onSuccess: () => spanContext.finishSpan('ok'),
                    onError: () => spanContext.finishSpan('internal_error'),
                });
        }
        // If there's no active Transaction simply foward the request
        case 'no-transaction':
        default:
            return fetcher => fetcher(url, fetchOptions);
    }
};

// Have to do JS -> unknown -> IAPIHelper to make TS happy for now since APIHelper is untyped
const RESTClient = createRestClient(
    APIHelper as unknown as IAPIHelper,
    handleApiError,
    SentryService,
    ignoredErrorCodes,
    tracingAugment,
);

export default RESTClient;
